<script>
    import { onMount, createEventDispatcher } from "svelte";
    import { getGroupTree, getGroupParent } from "../../../services/groupService";
    import { computeTreeLayout } from "../../../utils/treeLayout";
    import toast from "svelte-french-toast";

    export let groupId = undefined;
    export let selectedIds = [];

    const dispatch = createEventDispatcher();

    const NODE_W = 190;
    const NODE_H = 46;

    const typeColor = {
        Mondo: "#8b5cf6",
        Nazionale: "#ef4444",
        Regionale: "#f97316",
        Federazione: "#0ea5e9",
        Circolo: "#10b981"
    };

    let nodes = [];
    let edges = [];
    let collapsed = new Set();
    let isLoading = true;
    let selected = new Map(); // id -> node

    // pan / zoom
    let tx = 40;
    let ty = 40;
    let k = 1;
    let panning = false;
    let startX = 0;
    let startY = 0;
    let startTx = 0;
    let startTy = 0;

    $: layout = computeTreeLayout(nodes, edges, collapsed, { xGap: 240, yGap: 64 });

    // map each node to its parent (edge: source = parent, target = child)
    $: parentById = new Map(edges.map((e) => [e.target, e.source]));

    // node lookup by id, to inspect a node's parent
    $: nodeById = new Map(nodes.map((n) => [n.id, n]));

    // children grouped by parent id, to walk subtrees
    $: childrenByParent = edges.reduce((map, e) => {
        if (!map.has(e.source)) {
            map.set(e.source, []);
        }
        map.get(e.source).push(e.target);
        return map;
    }, new Map());

    // every node under the group we are managing (at any depth)
    $: groupDescendants = collectDescendants(groupId, childrenByParent);

    function collectDescendants(rootId, childrenMap) {
        const descendants = new Set();
        const stack = [...(childrenMap.get(rootId) ?? [])];

        while (stack.length) {
            const id = stack.pop();
            if (descendants.has(id)) {
                continue;
            }
            descendants.add(id);
            stack.push(...(childrenMap.get(id) ?? []));
        }

        return descendants;
    }

    function isSelectable(node) {
        // only circles that are direct children of a federation (not sons of
        // another circle), excluding any circle in the subtree of the group
        // whose jurisdiction we manage
        const parent = nodeById.get(parentById.get(node.id));

        return (
            node.type === "Circolo" &&
            !!parent &&
            parent.type !== "Circolo" &&
            !groupDescendants.has(node.id)
        );
    }

    function truncate(str, max) {
        if (!str) {
            return "";
        }
        return str.length > max ? `${str.slice(0, max - 1)}…` : str;
    }

    function toggle(node) {
        if (!node.hasChildren) {
            return;
        }

        const next = new Set(collapsed);
        next.has(node.id) ? next.delete(node.id) : next.add(node.id);
        collapsed = next;
    }

    function onNodeClick(node) {
        if (isSelectable(node)) {
            const next = new Map(selected);
            next.has(node.id) ? next.delete(node.id) : next.set(node.id, node);
            selected = next;
            dispatch("select", [...selected.values()]);
            return;
        }

        toggle(node);
    }

    function linkPath(l) {
        const x1 = l.source.x + NODE_W;
        const y1 = l.source.y + NODE_H / 2;
        const x2 = l.target.x;
        const y2 = l.target.y + NODE_H / 2;
        const mx = (x1 + x2) / 2;

        return `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`;
    }

    function onWheel(e) {
        e.preventDefault();

        const rect = e.currentTarget.getBoundingClientRect();
        const px = e.clientX - rect.left;
        const py = e.clientY - rect.top;
        const nk = Math.min(2.5, Math.max(0.2, k * (e.deltaY < 0 ? 1.1 : 0.9)));

        // keep the point under the cursor anchored while zooming
        tx = px - (px - tx) * (nk / k);
        ty = py - (py - ty) * (nk / k);
        k = nk;
    }

    function onMouseDown(e) {
        panning = true;
        startX = e.clientX;
        startY = e.clientY;
        startTx = tx;
        startTy = ty;
    }

    function onMouseMove(e) {
        if (!panning) {
            return;
        }
        tx = startTx + (e.clientX - startX);
        ty = startTy + (e.clientY - startY);
    }

    function onMouseUp() {
        panning = false;
    }

    function resetView() {
        tx = 40;
        ty = 40;
        k = 1;
    }

    onMount(async () => {
        try {
            const parentResp = await getGroupParent(groupId);
            const parentId = parentResp?.data?.id;

            const resp = await getGroupTree(parentId);
            nodes = resp?.data?.nodes ?? [];
            edges = resp?.data?.edges ?? [];

            selected = new Map(
                nodes.filter((n) => selectedIds.includes(n.id)).map((n) => [n.id, n])
            );
        } catch (err) {
            toast.error(`Errore durante il caricamento dell'albero, errore ${err.toString()}`);
        } finally {
            isLoading = false;
        }
    });
</script>

<svelte:window on:mousemove={onMouseMove} on:mouseup={onMouseUp} />

<div class="relative w-full h-full border border-blueGray-200 rounded overflow-hidden bg-blueGray-100">
    {#if isLoading}
        <div class="absolute inset-0 flex items-center justify-center text-blueGray-400">Caricamento…</div>
    {:else if !nodes.length}
        <div class="absolute inset-0 flex items-center justify-center text-blueGray-400">Nessun gruppo da mostrare</div>
    {:else}
        <button
            type="button"
            class="absolute top-2 right-2 z-10 text-xs bg-white border rounded px-2 py-1 shadow"
            on:click={resetView}
        >
            Reset vista
        </button>

        <svg
            class="w-full h-full"
            style="cursor: {panning ? 'grabbing' : 'grab'}"
            on:wheel={onWheel}
            on:mousedown={onMouseDown}
        >
            <g transform="translate({tx},{ty}) scale({k})">
                {#each layout.links as l}
                    <path d={linkPath(l)} fill="none" stroke="#cbd5e1" stroke-width="1.5" />
                {/each}

                {#each layout.nodes as n (n.id)}
                    <g
                        transform="translate({n.x},{n.y})"
                        style="cursor: {isSelectable(n) || n.hasChildren ? 'pointer' : 'default'}; opacity: {isSelectable(n) ? 1 : 0.55}"
                        on:click={() => onNodeClick(n)}
                    >
                        <rect
                            width={NODE_W}
                            height={NODE_H}
                            rx="6"
                            fill={selected.has(n.id) ? "#e0f2fe" : "#ffffff"}
                            stroke={selected.has(n.id) ? "#0ea5e9" : "#e2e8f0"}
                            stroke-width={selected.has(n.id) ? 2 : 1}
                        />
                        <rect width="5" height={NODE_H} rx="2" fill={typeColor[n.type] ?? "#94a3b8"} />

                        <text x="16" y="20" font-size="13" font-weight="600" fill="#334155">
                            {truncate(n.name, 22)}
                        </text>
                        <text x="16" y="35" font-size="10" fill="#94a3b8" style="text-transform: uppercase">
                            {n.type ?? ""}
                        </text>

                        {#if n.hasChildren}
                            <text
                                x={NODE_W - 16}
                                y="28"
                                font-size="14"
                                fill="#94a3b8"
                                text-anchor="middle"
                                on:click|stopPropagation={() => toggle(n)}
                            >
                                {n.collapsed ? "+" : "−"}
                            </text>
                        {/if}
                    </g>
                {/each}
            </g>
        </svg>
    {/if}
</div>
